@if (profile) {
  <div class="data-platform-profile-flyout">
    <!-- Condensed sticky header, fades in when the full header scrolls out of view -->
    <div class="mini-header-wrap">
      <div class="mini-header" [class.mini-header--visible]="showMiniHeader()">
        <app-person-avatar [email]="avatarEmail" [diameter]="18"></app-person-avatar>
        <span class="mini-header__title">{{ headerTitle }}</span>
        @if (profileScoreAvailable) {
          <span class="mini-header__score mini-header__score--{{ tier }}">
            <mat-icon class="mini-header__score-icon">{{ tierIcon }}</mat-icon>
            {{ profile.engagementScore }}
          </span>
        }
        <mat-icon class="mini-header__close" (click)="closeFlyout()">close</mat-icon>
      </div>
    </div>

    <!-- Back to visit, shown when this profile was opened from a visit flyout -->
    @if (backVisitDate) {
      <div class="back-crumb-bar">
        <button type="button" class="back-crumb" (click)="openBackVisit()">
          <mat-icon class="back-crumb__icon">arrow_back</mat-icon>
          <span>{{ "data-platform.back-to-visit" | translate }}: {{ backVisitDate | formatDate }}</span>
        </button>
      </div>
    }

    <!-- Header -->
    <div class="flyout-header" #flyoutHeaderEl>
      <div class="flyout-header-left">
        <app-person-avatar [email]="avatarEmail" [diameter]="32"></app-person-avatar>
        <div class="header-text">
          <div class="header-title">{{ headerTitle }}</div>
          @if (headerSubtitle) {
            <div class="header-subtitle">{{ headerSubtitle }}</div>
          }
        </div>
      </div>
      <div class="header-actions">
        <button
          mat-icon-button
          class="header-menu-btn"
          [matMenuTriggerFor]="profileMenu"
          [disabled]="isDeleting"
          data-test="profile-actions"
          [attr.aria-label]="'data-platform.profile-actions' | translate">
          <mat-icon>more_vert</mat-icon>
        </button>
        <mat-menu #profileMenu="matMenu">
          @if (canUnmerge()) {
            <button mat-menu-item (click)="openUnmerge()" data-test="unmerge-profile">
              <mat-icon>call_split</mat-icon>
              <span class="unmerge-menu-label">
                {{ "data-platform.unmerge-profile" | translate }}
                <mat-icon class="unmerge-admin-tag">font_download</mat-icon>
              </span>
            </button>
          }
          <button mat-menu-item class="danger-menu-item" (click)="confirmDeleteProfile()" data-test="delete-profile">
            <mat-icon>delete</mat-icon>
            <span>{{ "data-platform.delete-profile" | translate }}</span>
          </button>
        </mat-menu>
        <mat-icon class="close-icon" (click)="closeFlyout()">close</mat-icon>
      </div>
    </div>

    @if (isDeleting) {
      <mat-progress-bar mode="indeterminate" data-test="delete-profile-progress"></mat-progress-bar>
    }

    <!-- Intent Score Card — hidden when the profile was derived from visits (no authoritative score) -->
    @if (profileScoreAvailable) {
      <div class="score-card score-card--{{ tier }}">
        <div class="score-card-top">
          <span class="score-card-label-group">
            <span class="score-card-label">{{ "data-platform.engagement-score" | translate }}</span>
            <mat-icon
              class="score-card-info"
              tabindex="0"
              aria-hidden="false"
              [attr.aria-label]="'data-platform.engagement-profile-score-info' | translate"
              [matTooltip]="'data-platform.engagement-profile-score-info' | translate"
              matTooltipClass="multiline-tooltip long-value-tooltip"
              data-test="profile-score-info">
              info_outline
            </mat-icon>
          </span>
          <span class="score-card-badge">
            <mat-icon class="score-card-badge-icon">{{ tierIcon }}</mat-icon>
            {{ tierLabelKey | translate }}
          </span>
        </div>
        <div class="score-card-body">
          <span class="score-card-number">{{ profile.engagementScore }}</span>
          <div class="score-card-track">
            <div class="score-card-fill" [style.width.%]="profile.engagementScore"></div>
          </div>
        </div>
        @if (scoreTrend(); as trend) {
          <div class="score-card-trend">
            <svg viewBox="0 0 110 26" class="score-card-trend__svg" aria-hidden="true">
              <polyline [attr.points]="trend.points" class="score-card-trend__line" />
              <circle [attr.cx]="trend.endX" [attr.cy]="trend.endY" r="2.4" class="score-card-trend__end" />
            </svg>
            <span class="score-card-trend__label">
              {{ "data-platform.score-trend" | translate: { count: trend.count } }}
            </span>
          </div>
        }
      </div>
    }

    <!-- Activity Card: recency (last seen) + tenure (first seen) -->
    @if (profile.lastSeen) {
      <div class="activity-card">
        <div class="activity-card__top">
          <span class="activity-card__label">{{ "data-platform.activity" | translate }}</span>
          @if (hasOngoingVisit()) {
            <span class="activity-card__badge activity-card__badge--ongoing" [matTooltip]="'data-platform.ongoing-visit-hint' | translate">
              <span class="ongoing-dot"></span>
              {{ "data-platform.ongoing" | translate }}
            </span>
          } @else {
            <span class="activity-card__badge activity-card__badge--{{ recencyBucket }}">
              {{ recencyBadgeKey | translate }}
            </span>
          }
        </div>
        <div class="activity-card__hero">
          {{ hasOngoingVisit() ? ("data-platform.now" | translate) : (lastSeenDate | formatDate) }}
        </div>
        <div class="activity-scale" aria-hidden="true">
          <div class="activity-scale__seg">
            <div class="activity-scale__bar" [class.activity-scale__bar--active]="recencyBucket === 'week'">
              @if (recencyBucket === "week") {
                <span class="activity-scale__dot" [style.left.%]="recencyDotLeft"></span>
              }
            </div>
            <span class="activity-scale__lab">≤ 7d</span>
          </div>
          <div class="activity-scale__seg">
            <div class="activity-scale__bar" [class.activity-scale__bar--active]="recencyBucket === 'month'">
              @if (recencyBucket === "month") {
                <span class="activity-scale__dot" [style.left.%]="recencyDotLeft"></span>
              }
            </div>
            <span class="activity-scale__lab">≤ 30d</span>
          </div>
          <div class="activity-scale__seg">
            <div class="activity-scale__bar" [class.activity-scale__bar--active]="recencyBucket === 'inactive'">
              @if (recencyBucket === "inactive") {
                <span class="activity-scale__dot" [style.left.%]="recencyDotLeft"></span>
              }
            </div>
            <span class="activity-scale__lab">30d+</span>
          </div>
        </div>
        @if (profile.firstSeen) {
          <div class="activity-card__foot">
            <span class="activity-card__foot-label">{{ "data-platform.first-seen" | translate }}</span>
            <span class="activity-card__foot-value">
              {{ tenureKey | translate: { count: tenureCount } }}
              <span class="activity-card__foot-exact">· {{ firstSeenDate | formatDate }}</span>
            </span>
          </div>
        }
      </div>
    }

    <!-- Audience Insights -->
    @if (matchedInsights().length > 0) {
      <div class="chips-label">{{ "data-platform.audiences" | translate }}</div>
      <div class="insight-chips">
        @for (insight of matchedInsights(); track insight.id) {
          <span class="insight-chip insight-chip--{{ insight.variant }}">
            <mat-icon class="insight-chip-icon">{{ insight.icon }}</mat-icon>
            {{ insight.label }}
          </span>
        }
      </div>
    }

    <!-- General Info -->
    <div class="flyout-row">
      <div class="flyout-data-group">
        <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
          <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
            <img class="header-icon" src="/assets/img/icons_fl/user-analytics.svg" width="32px" height="32px" />
            <span>{{ "data-platform.general" | translate }}</span>
          </mat-expansion-panel-header>

          <div class="expansion-panel-row no-border">
            <div class="label">
              <span>{{ "data-platform.total-events" | translate }}</span>
            </div>
            <div class="value">
              <span>{{ profile.totalEvents | toLocaleNumber }}</span>
            </div>
          </div>

          <div class="expansion-panel-row no-border">
            <div class="label">
              <span>{{ "data-platform.sessions" | translate }}</span>
            </div>
            <div class="value">
              <span>{{ profile.totalSessions | toLocaleNumber }}</span>
            </div>
          </div>

          @if (topDimensionDisplay) {
            <div class="expansion-panel-row no-border">
              <div class="label">
                <span>{{ "data-platform.top-dimension" | translate }}</span>
              </div>
              <div class="value">
                <span class="dim-chip">{{ topDimensionDisplay }}</span>
              </div>
            </div>
          }

          @if (profile.lastGeoCountry) {
            <div class="expansion-panel-row no-border">
              <div class="label">
                <span>{{ "flyout.general.country" | translate }}</span>
              </div>
              <div class="value">
                <span class="flag-{{ profile.lastGeoCountry | lowercase }}"></span>
                <span>{{ profile.lastGeoCountry }}{{ profile.lastGeoCity ? ", " + profile.lastGeoCity : "" }}</span>
              </div>
            </div>
          }

          @if (profile.tags?.length > 0) {
            <div class="expansion-panel-row no-border">
              <div class="label">
                <span>{{ "data-platform.tags" | translate }}</span>
              </div>
              <div class="value tags-value">
                @for (tag of profile.tags; track tag) {
                  <span class="tag-chip">{{ tag }}</span>
                }
              </div>
            </div>
          }
        </mat-expansion-panel>
      </div>
    </div>

    <!-- Interests (Dimension Affinity across all visits) -->
    <div class="flyout-row">
      <div class="flyout-data-group">
        <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
          <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
            <mat-icon class="section-icon">category</mat-icon>
            <span>{{ "data-platform.dimensions" | translate }}</span>
          </mat-expansion-panel-header>

          @if (isLoadingVisits()) {
            <div class="section-skeleton">
              @for (width of [55, 70, 45]; track $index) {
                <div class="section-skeleton__row">
                  <span class="section-skeleton__cell" [style.width.%]="width"></span>
                  <span class="section-skeleton__cell section-skeleton__cell--end"></span>
                </div>
              }
            </div>
          } @else if (dimensionTypeDonut().length === 0) {
            <div class="expansion-panel-row no-border interests-empty">
              <span>{{ "data-platform.no-interest-data" | translate }}</span>
            </div>
          } @else {
            @for (group of dimensionTypeDonut(); track group.type) {
              @let segments = group.showAll ? group.allSegments : group.segments;
              <div class="dim-type-section">
                <div class="dim-type-label">{{ group.displayType }}</div>
                <div class="dim-affinity-panel">
                  <svg viewBox="0 0 40 40" class="dim-donut" aria-hidden="true">
                    <circle cx="20" cy="20" r="15.9155" fill="transparent" stroke="var(--cardDivider)" stroke-width="4" />
                    @for (seg of segments; track $index) {
                      <circle
                        cx="20"
                        cy="20"
                        r="15.9155"
                        fill="transparent"
                        [attr.stroke]="seg.color"
                        stroke-width="4"
                        [attr.stroke-dasharray]="seg.dashArray"
                        [attr.stroke-dashoffset]="seg.dashOffset"
                        [matTooltip]="
                          'data-platform.dimension-legend-tooltip' | translate: { dimension: seg.dimension, count: seg.count, percentage: seg.percentage }
                        "></circle>
                    }
                  </svg>
                  <ul class="dim-legend">
                    @for (seg of segments; track $index) {
                      <li class="dim-legend-item" [matTooltip]="seg.membersTooltip">
                        <span class="dim-legend-dot" [style.background]="seg.color"></span>
                        <span class="dim-legend-label">{{ seg.dimension }}</span>
                        <span class="dim-legend-pct">{{ seg.percentage }}%</span>
                        <span class="dim-legend-count">({{ seg.count | toLocaleNumber }})</span>
                      </li>
                    }
                  </ul>
                </div>
                @if (group.hiddenCount > 0) {
                  <button type="button" class="dim-show-more" (click)="group.showAll = !group.showAll">
                    {{ (group.showAll ? "data-platform.dimension-show-less" : "data-platform.dimension-show-more") | translate: { count: group.hiddenCount } }}
                  </button>
                }
              </div>
            }
          }
        </mat-expansion-panel>
      </div>
    </div>

    <!-- Purchases -->
    <app-data-platform-purchases-list
      [purchases]="purchaseItems()"
      [totalRevenue]="allTimeTotalRevenue()"
      [currency]="currency"
      [isLoading]="isLoadingPurchases()"
      [showSummaryCards]="true"
      [interactive]="true"
      (purchaseSelected)="openPurchaseVisit($event)"></app-data-platform-purchases-list>

    <!-- Goals -->
    @if (profileGoalItems().length > 0) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <mat-icon class="section-icon">flag</mat-icon>
              <span>{{ "data-platform.goals" | translate }} ({{ profileGoalItems().length }})</span>
            </mat-expansion-panel-header>

            <app-collapsible-list [items]="profileGoalItems()" [itemTemplate]="goalRow"></app-collapsible-list>
            <ng-template #goalRow let-goal>
              <div class="expansion-panel-row values goal-row">
                <div class="label goal-label">
                  <mat-icon class="goal-icon-small">{{ goal.icon }}</mat-icon>
                  <span class="goal-name" [title]="goal.name">{{ goal.name }}</span>
                  @if (goal.count > 1) {
                    <span class="goal-count">×{{ goal.count }}</span>
                  }
                </div>
              </div>
            </ng-template>
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Distributed Coupons (aggregated + deduped across this profile's visits) -->
    @if (distributedCoupons().length > 0) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <mat-icon class="section-icon">local_offer</mat-icon>
              <span>{{ "data-platform.distributed-coupons" | translate }} ({{ distributedCoupons().length }})</span>
            </mat-expansion-panel-header>

            <app-collapsible-list [items]="distributedCoupons()" [itemTemplate]="couponRow"></app-collapsible-list>
            <ng-template #couponRow let-coupon>
              <div class="expansion-panel-row no-border coupon-row">
                <div class="coupon-row__code">
                  @if (coupon.code) {
                    <span>{{ coupon.code }}</span>
                  } @else {
                    <span class="coupon-row__placeholder">{{ "data-platform.coupon-code-placeholder" | translate }}</span>
                  }
                </div>
                <div class="value coupon-row__state">
                  @if (coupon.isCopied) {
                    <span class="coupon-status coupon-status--copied">
                      <mat-icon class="coupon-status__icon">check_circle</mat-icon>
                      {{ "data-platform.coupon-copied" | translate }}
                    </span>
                  } @else {
                    <span class="coupon-status coupon-status--not-copied">
                      <mat-icon class="coupon-status__icon">highlight_off</mat-icon>
                      {{ "data-platform.coupon-not-copied" | translate }}
                    </span>
                  }
                </div>
              </div>
            </ng-template>
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Traits -->
    @if (traitsEntries().length > 0) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <mat-icon class="section-icon">person_pin</mat-icon>
              <span>{{ "data-platform.properties" | translate }}</span>
            </mat-expansion-panel-header>

            @for (trait of traitsEntries(); track trait.key) {
              <div class="expansion-panel-row values trait-row" [class.trait-row--identifier]="trait.isIdentifierValue">
                <div class="label">
                  <span [title]="trait.key">{{ trait.label }}</span>
                </div>
                <div class="value">
                  <span [attr.title]="trait.isIdentifierValue ? trait.value : null">{{ trait.value }}</span>
                </div>
              </div>
            }
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Consent Events -->
    <app-data-platform-consent-list [consentEvents]="consentEvents"></app-data-platform-consent-list>

    <!-- Visits -->
    @if (flyoutOpeningData?.dataPlatformFilter) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <mat-icon class="section-icon">history</mat-icon>
              <span>{{ "data-platform.visits" | translate }}</span>
            </mat-expansion-panel-header>

            @if (isLoadingVisits()) {
              <div class="section-skeleton">
                @for (width of [60, 45, 70]; track $index) {
                  <div class="section-skeleton__row">
                    <span class="section-skeleton__cell" [style.width.%]="width"></span>
                    <span class="section-skeleton__cell section-skeleton__cell--end"></span>
                  </div>
                }
              </div>
            } @else if (visitSummaries().length === 0) {
              <div class="expansion-panel-row no-border visits-empty">
                <span>{{ "data-platform.no-profile-visits" | translate }}</span>
              </div>
            } @else {
              <app-collapsible-list [items]="visitRows()" [itemTemplate]="visitRow"></app-collapsible-list>
              <ng-template #visitRow let-summary>
                <div
                  class="expansion-panel-row visit-row visit-row--clickable"
                  role="button"
                  tabindex="0"
                  [matTooltip]="'data-platform.open-visit' | translate"
                  (click)="openVisit(summary)"
                  (keydown.enter)="openVisit(summary)"
                  (keydown.space)="openVisit(summary); $event.preventDefault()">
                  <span class="visit-tier-dot visit-tier-dot--{{ summary.tier }}" [matTooltip]="summary.tierTooltip"></span>
                  <div class="visit-date">{{ summary.startedAtDate | formatDate }}</div>
                  <div class="visit-meta">
                    <span class="visit-stat">
                      {{
                        (summary.pageCount === 1 ? "data-platform.visit-page-count-one" : "data-platform.visit-page-count")
                          | translate: { count: (summary.pageCount | toLocaleNumber) }
                      }}
                    </span>
                    <span class="visit-stat">
                      {{
                        (summary.goalCount === 1 ? "data-platform.visit-goal-count-one" : "data-platform.visit-goal-count")
                          | translate: { count: (summary.goalCount | toLocaleNumber) }
                      }}
                    </span>
                    @if (summary.revenue > 0) {
                      <span class="visit-stat visit-revenue">{{ summary.revenue | formatCurrency: currency }}</span>
                    } @else if (summary.hasPurchaseWithoutRevenue) {
                      <span class="visit-stat visit-purchased">{{ "data-platform.purchased" | translate }}</span>
                    }
                    @if (summary.isOngoing) {
                      <span class="ongoing-badge">
                        <span class="ongoing-badge__dot"></span>
                        {{ "data-platform.ongoing" | translate }}
                      </span>
                    } @else {
                      <span class="visit-stat visit-duration">{{ summary.duration }}</span>
                    }
                  </div>
                  <mat-icon class="visit-row-chevron">chevron_right</mat-icon>
                </div>
              </ng-template>
            }
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Identifiers (lazy-loaded via /profiles/details) -->
    @if (identifierRows().length > 0) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <mat-icon class="section-icon">mail</mat-icon>
              <span>{{ "data-platform.identifiers" | translate }}</span>
            </mat-expansion-panel-header>

            @for (id of identifierRows(); track id.value) {
              <div class="expansion-panel-row no-border identifier-row">
                <div class="value">
                  <span class="identifier-value">{{ id.value }}</span>
                  <button type="button" class="copy-btn" [matTooltip]="'generic.copyToClipboard.copy' | translate" (click)="copyValue(id.value)">
                    <mat-icon class="copy-btn__icon">content_copy</mat-icon>
                  </button>
                  @if (id.badge; as badge) {
                    <span class="source-badge" [matTooltip]="id.sourceTooltip">
                      <span>{{ badge.labelKey | translate }}</span>
                      @if (badge.logoUrl) {
                        <img class="source-badge__logo" [src]="badge.logoUrl" alt="" />
                      } @else {
                        <mat-icon class="source-badge__icon">{{ badge.icon }}</mat-icon>
                      }
                    </span>
                  }
                </div>
              </div>
            }
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Devices (UID + browser/OS + last seen) -->
    @if (deviceViews.length > 0) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="true" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <mat-icon class="section-icon">devices</mat-icon>
              <span>{{ "data-platform.devices" | translate }} ({{ deviceViews.length }})</span>
            </mat-expansion-panel-header>

            <app-collapsible-list [items]="deviceViews" [itemTemplate]="deviceRow"></app-collapsible-list>
            <ng-template #deviceRow let-device>
              <div class="device-row">
                <div class="device-row-top">
                  <span class="device-uid">UID {{ device.uid }}</span>
                  <span class="device-last-seen">{{ device.lastSeenDate | formatDate }}</span>
                </div>
                @if (device.browser || device.os) {
                  <div class="device-row-bottom">
                    @if (device.browser) {
                      <span class="device-chip">
                        <app-browser-icon [browser]="device.browser"></app-browser-icon>
                        <span>{{ device.browser }}</span>
                      </span>
                    }
                    @if (device.os) {
                      <span class="device-chip">
                        <app-os-icon [os]="device.os"></app-os-icon>
                        <span>{{ device.os }}</span>
                      </span>
                    }
                  </div>
                }
              </div>
            </ng-template>
          </mat-expansion-panel>
        </div>
      </div>
    }

    <!-- Source / Campaign (lazy-loaded via /profiles/details, from latest visit) -->
    @if (
      source &&
      (source.lastSessionSource ||
        source.lastReferrerApp ||
        source.lastReferrerDomain ||
        source.lastReferrerType ||
        source.lastUtmCampaignName ||
        source.lastUtmCampaignSource ||
        source.lastUtmCampaignMedium)
    ) {
      <div class="flyout-row">
        <div class="flyout-data-group">
          <mat-expansion-panel [expanded]="false" class="mat-elevation-z0">
            <mat-expansion-panel-header [collapsedHeight]="'48px'" [expandedHeight]="'48px'">
              <img class="header-icon" src="/assets/img/icons_fl/source.svg" width="32px" height="32px" />
              <span>{{ "flyout.source.title" | translate }}</span>
            </mat-expansion-panel-header>

            @if (source.lastSessionSource) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "data-platform.session-source" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ "data-platform.session-source-values." + source.lastSessionSource | translate }}</span>
                </div>
              </div>
            }

            @if (source.lastReferrerApp) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "data-platform.referrer-app" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ "data-platform.referrer-app-values." + source.lastReferrerApp | translate }}</span>
                </div>
              </div>
            }

            @if (source.lastReferrerType) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "flyout.source.referer-type" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ source.lastReferrerType }}</span>
                </div>
              </div>
            }

            @if (source.lastReferrerDomain) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "flyout.source.referer" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ source.lastReferrerDomain }}</span>
                </div>
              </div>
            }

            @if (source.lastUtmCampaignName) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "data-platform.campaign" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ source.lastUtmCampaignName }}</span>
                </div>
              </div>
            }

            @if (source.lastUtmCampaignSource) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "flyout.source.utm-source" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ source.lastUtmCampaignSource }}</span>
                </div>
              </div>
            }

            @if (source.lastUtmCampaignMedium) {
              <div class="expansion-panel-row values">
                <div class="label">
                  <span>{{ "flyout.source.utm-medium" | translate }}:</span>
                </div>
                <div class="value">
                  <span>{{ source.lastUtmCampaignMedium }}</span>
                </div>
              </div>
            }
          </mat-expansion-panel>
        </div>
      </div>
    }
  </div>
}

@if (!profile && isLoadingProfile()) {
  <div class="data-platform-profile-flyout profile-flyout-loading">
    <div class="flyout-header">
      <div class="flyout-header-left">
        <mat-icon class="header-icon">person</mat-icon>
        <div class="header-text">
          <div class="header-title">{{ "data-platform.profile-details" | translate }}</div>
        </div>
      </div>
      <mat-icon class="close-icon" (click)="closeFlyout()">close</mat-icon>
    </div>
    <div class="section-skeleton section-skeleton--page">
      @for (width of [65, 45, 75, 55, 40]; track $index) {
        <div class="section-skeleton__row">
          <span class="section-skeleton__cell" [style.width.%]="width"></span>
          <span class="section-skeleton__cell section-skeleton__cell--end"></span>
        </div>
      }
    </div>
  </div>
}
